import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CONTENT_SCHEMA, SCHEMA_BY_KEY } from "@/lib/contentSchema";
import RichTextField from "@/components/RichTextField";
import { ChevronRight, Save, ArrowLeft, ExternalLink, Rocket, History, RotateCcw, Eye, Trash2 } from "lucide-react";

// -------- List of editable pages --------
export function AdminContent() {
  const [status, setStatus] = useState({});
  useEffect(() => {
    // Best-effort: fetch each page's admin doc to show draft/published state.
    Promise.all(
      CONTENT_SCHEMA.map((p) =>
        api.get(`/admin/content/${p.key}`).then((r) => [p.key, r.data]).catch(() => [p.key, null])
      )
    ).then((rows) => setStatus(Object.fromEntries(rows)));
  }, []);

  return (
    <div data-testid="admin-content-list">
      <div className="eyebrow mb-2">Content</div>
      <h1 className="display text-[36px] mb-2">Rewrite copy</h1>
      <p className="text-[15px] text-brand-ink/60 max-w-xl mb-10">
        Every headline, eyebrow, and section title across the site can be
        rewritten here. Save as a draft, preview privately, then publish. The
        last 10 published versions of each page are kept — restorable in a
        click.
      </p>

      <div className="grid gap-3 max-w-2xl">
        {CONTENT_SCHEMA.map((p) => {
          const s = status[p.key];
          const draftKeys = s ? Object.keys(s.draft || {}) : [];
          const pubKeys = s ? Object.keys(s.published || {}) : [];
          const hasDraft = draftKeys.length > 0 && JSON.stringify(s.draft) !== JSON.stringify(s.published);
          const hasPub = pubKeys.length > 0;
          return (
            <Link
              key={p.key}
              to={`/admin/content/${p.key}`}
              data-testid={`content-page-${p.key}`}
              className="group flex items-center justify-between rounded-lg border border-brand-rule/70 bg-brand-cream px-5 py-4 hover:border-brand-blue/50 transition-colors"
            >
              <div>
                <div className="font-serif text-[19px] text-brand-ink">{p.label}</div>
                <div className="text-[12.5px] text-brand-ink/50 mt-0.5">
                  {p.fields.length} editable fields · {p.path}
                </div>
              </div>
              <div className="flex items-center gap-3">
                {hasDraft && <span className="text-[11px] px-2 py-1 rounded-full bg-brand-yellow/20 text-brand-ink/80">Draft</span>}
                {hasPub && <span className="text-[11px] px-2 py-1 rounded-full bg-brand-turquoise/15 text-brand-turquoise">Live</span>}
                <ChevronRight size={16} className="text-brand-ink/40 group-hover:text-brand-blue group-hover:translate-x-1 transition-all" />
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

// -------- Per-page editor --------
export function AdminContentEditor() {
  const { pageKey } = useParams();
  const nav = useNavigate();
  const schema = SCHEMA_BY_KEY[pageKey];
  const [doc, setDoc] = useState(null);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    const r = await api.get(`/admin/content/${pageKey}`);
    setDoc(r.data);
    setValues(r.data.draft && Object.keys(r.data.draft).length ? r.data.draft : (r.data.published || {}));
  };
  useEffect(() => {
    if (!schema) return;
    reload().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageKey]);

  if (!schema) return <div className="text-brand-ink/60">Unknown page.</div>;

  const saveDraft = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/content/${pageKey}/draft`, { fields: values });
      toast.success("Draft saved.");
      await reload();
    } catch { toast.error("Save failed."); } finally { setBusy(false); }
  };

  const publish = async () => {
    if (!window.confirm("Publish the current draft? A backup of the previous live version will be kept in history.")) return;
    setBusy(true);
    try {
      await api.put(`/admin/content/${pageKey}/draft`, { fields: values });
      await api.post(`/admin/content/${pageKey}/publish`);
      toast.success("Published.");
      await reload();
    } catch { toast.error("Publish failed."); } finally { setBusy(false); }
  };

  const discard = async () => {
    if (!window.confirm("Discard your unsaved draft and revert to the live version?")) return;
    setBusy(true);
    try {
      await api.post(`/admin/content/${pageKey}/discard-draft`);
      toast.success("Draft discarded.");
      await reload();
    } catch { toast.error("Failed."); } finally { setBusy(false); }
  };

  const restore = async (idx) => {
    if (!window.confirm("Restore this version into your working draft? You'll still need to Publish to make it live.")) return;
    setBusy(true);
    try {
      await api.post(`/admin/content/${pageKey}/restore/${idx}`);
      toast.success("Version restored to draft.");
      await reload();
    } catch { toast.error("Restore failed."); } finally { setBusy(false); }
  };

  const hasChangesVsDraft = doc ? JSON.stringify(values) !== JSON.stringify(doc.draft || {}) : false;
  const draftDiffersFromLive = doc ? JSON.stringify(doc.draft || {}) !== JSON.stringify(doc.published || {}) && Object.keys(doc.draft || {}).length > 0 : false;

  return (
    <div data-testid="admin-content-editor">
      <button onClick={() => nav("/admin/content")} className="inline-flex items-center gap-1.5 text-[13px] text-brand-ink/60 hover:text-brand-blue mb-6" data-testid="content-editor-back">
        <ArrowLeft size={14} /> All pages
      </button>

      <div className="flex items-baseline justify-between mb-8 gap-6 flex-wrap">
        <div>
          <div className="eyebrow mb-2">Editing</div>
          <h1 className="display text-[36px]">{schema.label}</h1>
          <div className="mt-2 text-[12.5px] text-brand-ink/55">
            {doc?.published_at && <>Live version published {new Date(doc.published_at).toLocaleString()} · </>}
            {doc?.draft_updated_at && <>Draft last saved {new Date(doc.draft_updated_at).toLocaleString()}</>}
          </div>
        </div>
        <div className="flex gap-3">
          <a href={`${schema.path}?preview=1`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[13px] px-3.5 py-2 rounded-full border border-brand-ink/25 text-brand-ink hover:border-brand-blue hover:text-brand-blue" data-testid="preview-draft-btn">
            <Eye size={13} /> Preview draft
          </a>
          <a href={schema.path} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[13px] text-brand-blue hover:text-brand-ink self-center" data-testid="view-live-btn">
            <ExternalLink size={12} /> View live
          </a>
        </div>
      </div>

      {loading ? (
        <p className="text-brand-ink/50 text-[14px]">Loading…</p>
      ) : (
        <div className="grid gap-8 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
          <form onSubmit={(e) => { e.preventDefault(); saveDraft(); }} className="space-y-5" data-testid="content-editor-form">
            {schema.fields.map((f) => (
              <div key={f.key}>
                <label className="block text-[12px] text-brand-ink/60 mb-1.5 uppercase tracking-wider">{f.label}</label>
                <RichTextField
                  value={values[f.key] || ""}
                  onChange={(v) => setValues({ ...values, [f.key]: v })}
                  rows={f.type === "textarea" ? 3 : 1}
                  singleLine={f.type !== "textarea"}
                  placeholder="Leave blank to use the site default."
                  testId={`field-${f.key}`}
                />
              </div>
            ))}

            <div className="pt-4 flex items-center gap-3 flex-wrap sticky bottom-0 bg-background/80 backdrop-blur py-3 -mx-3 px-3 border-t border-brand-rule/40">
              <button type="submit" disabled={busy || !hasChangesVsDraft} data-testid="save-draft-btn" className="inline-flex items-center gap-2 rounded-full bg-brand-ink px-5 py-2.5 text-brand-cream text-[13.5px] hover:bg-brand-blue transition-colors disabled:opacity-40">
                <Save size={13} /> Save draft
              </button>
              <button type="button" onClick={publish} disabled={busy} data-testid="publish-btn" className="inline-flex items-center gap-2 rounded-full bg-brand-turquoise px-5 py-2.5 text-white text-[13.5px] hover:brightness-90 transition-all disabled:opacity-40">
                <Rocket size={13} /> Publish
              </button>
              {draftDiffersFromLive && (
                <button type="button" onClick={discard} disabled={busy} data-testid="discard-btn" className="inline-flex items-center gap-1.5 text-[13px] text-brand-ink/60 hover:text-red-600" title="Discard unsaved draft">
                  <Trash2 size={13} /> Discard draft
                </button>
              )}
            </div>
          </form>

          <aside className="rounded-lg border border-brand-rule/60 bg-brand-cream p-6 h-fit" data-testid="version-history">
            <div className="flex items-center gap-2 mb-4">
              <History size={14} className="text-brand-ink/60" />
              <div className="eyebrow">Version history</div>
            </div>
            {(!doc?.history || doc.history.length === 0) ? (
              <p className="text-[13px] text-brand-ink/50">Once you publish twice, older versions will appear here — up to the last 10.</p>
            ) : (
              <ul className="space-y-2">
                {doc.history.map((h, i) => (
                  <li key={i} className="flex items-center justify-between text-[13px] py-2 border-b border-brand-rule/40 last:border-0" data-testid={`history-row-${i}`}>
                    <div>
                      <div className="text-brand-ink">Version {doc.history.length - i}</div>
                      <div className="text-[11.5px] text-brand-ink/50">{new Date(h.archived_at).toLocaleString()}</div>
                    </div>
                    <button onClick={() => restore(i)} data-testid={`restore-${i}`} className="inline-flex items-center gap-1 text-[12px] text-brand-blue hover:text-brand-ink" title="Restore this version into draft">
                      <RotateCcw size={11} /> Restore
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </aside>
        </div>
      )}
    </div>
  );
}

export default AdminContent;
